import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useEffect, useRef } from 'react';
import { addComma } from '../../../../lib/common';
import { GRAY, LIGHT_BLUE, ORANGE } from '../../../../styles/constants/color';
import { fontFrame } from '../../../../styles/mixin/font';
import { SimpleItem } from '../../../../types/common/modal/Item';
import Span from '../../text/Span';

const ItemList = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  width: 100%;
  height: auto;
  max-height: 425px;
  background-color: transparent;
  overflow-y: overlay;
  position: relative;
`;

const Item = styled.li<{
  itemMaxWidth: string;
  isSelected: boolean;
  cols: number;
  isDisabled: boolean;
  isAllCenter: boolean;
}>`
  display: grid;
  grid-template-columns: ${props => (props.cols === 1 ? 'repeat(1, 1fr)' : 'unset')};
  width: auto;
  height: auto;
  min-width: 250px;
  min-height: 55px;
  max-width: ${props => props.itemMaxWidth};
  border: 1px solid ${props => (props.isSelected ? LIGHT_BLUE : GRAY)};
  border-radius: 6px;
  padding: 8px;
  position: relative;
  cursor: pointer;

  ${props =>
    props.isSelected &&
    css`
      & > div > span {
        color: ${LIGHT_BLUE};
      }
    `}

  ${props =>
    props.isDisabled &&
    css`
      & > div > span {
        color: ${GRAY};
      }
    `}
`;

const TextBox = styled.div<{ jfContents?: string; existPrice: boolean }>`
  align-self: ${props => (props.existPrice ? 'end' : 'center')};
  justify-self: ${props => props.jfContents};

  & > span {
    display: block;
  }
`;

const SubSpan = styled.span<{ isPrice?: boolean }>`
  ${props =>
    props.isPrice
      ? css`
          ${fontFrame('500', '14px', '1', ORANGE)};
          grid-column: span 2;
          place-self: end;
        `
      : css`
          margin-top: 4px;
          ${fontFrame('300', '12px', '1', '#888')};
        `}
`;

interface Props<T> {
  list: SimpleItem<T>[];
  selectedNo: number;
  itemMaxWidth?: string;
  justifySelf?: string;
  isAllCenter?: boolean;
  // onClick: <T>(param: T, id?: number) => void;
  onClick: <T>(param: any, id?: number) => void;
}

function SimpleListContents<T>({
  list,
  selectedNo,
  itemMaxWidth = 'auto',
  justifySelf = 'center',
  isAllCenter = false,
  onClick,
}: Props<T>): JSX.Element {
  const listRef = useRef<HTMLUListElement>(null);
  const itemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (selectedNo) {
      // 선택된 아이템이 있으면 그 아이템으로 scrollTo 해준다.
      const listElement: HTMLElement | null = listRef.current;
      const selectedElement: HTMLElement | null = itemRef.current;

      if (listElement && selectedElement) {
        const offsetTop: number = selectedElement.offsetTop;
        const offsetHeight: number = selectedElement.offsetHeight;
        const middleOfItem: number = offsetTop + offsetHeight / 2;
        listElement.scrollTo(0, middleOfItem - (offsetHeight + 8) * 3);
      }
    }
  }, []);
  return (
    <ItemList ref={listRef}>
      {!!list?.length &&
        list.map((item: SimpleItem<T>) => (
          <Item
            ref={selectedNo === item.id ? itemRef : null}
            key={item.id}
            itemMaxWidth={itemMaxWidth}
            onClick={() => {
              if (!item.disabled) {
                document.body.style.overflow = 'auto';
                onClick<T>(item.param, item.id);
              }
            }}
            isSelected={selectedNo === item.id}
            cols={item.price ? 2 : 1}
            isDisabled={item.disabled || false}
            isAllCenter={isAllCenter}
          >
            <TextBox jfContents={justifySelf} existPrice={!!item.price && !isAllCenter}>
              <Span fontSize='16px' fontWeight='400'>
                {item.name} {!!item.price && isAllCenter && `- ${addComma(item.price)}`}
              </Span>
              {item.subText && <SubSpan>{item.subText}</SubSpan>}
            </TextBox>
            {!!item.price && !isAllCenter && <SubSpan isPrice>{addComma(item.price)}</SubSpan>}
          </Item>
        ))}
    </ItemList>
  );
}

/**
 *  심플한 리스트 컴포넌트
 * @param {SimpleItem[]} list 아이템 목록
 * @param {number} selectedNo 선택된 아이템 번호
 * @param {string} itemMaxWidth 옵션 하나의 로우 max-width (default: auto)
 * @param {string} justifySelf item 배치 방향( start, center, end, default: center)
 * @param {boolean} isAllCenter 아이템 하나 모두를 가운데 정렬 여부
 * @param {function( param: T, id?: number)} onClick item onClick 이벤트 함수
 *  */
export default React.memo(SimpleListContents);
